import { createApi } from '@reduxjs/toolkit/query/react'
import { createAuthBaseQuery } from './base-query'
import { API } from '@/config/constants'
import {
  strategySchema,
  strategyListItemSchema,
  strategyVersionSchema,
  strategyVariantSchema,
  snippetSchema,
  backtestResultSchema,
  journalEntrySchema,
  filterPresetSchema,
  strategyMetricsSchema,
} from '@/features/backtest/schemas/backtest-types'
import type {
  Strategy,
  StrategyListItem,
  StrategyScript,
  StrategyVersion,
  StrategyVariant,
  Snippet,
  BacktestResultRecord,
  JournalEntry,
  FilterPreset,
  StrategyMetrics,
} from '@/features/backtest/schemas/backtest-types'
import type {
  ExecutionContext,
  ExecutionRecord,
  MergeResult,
  RunConfig,
} from '@/services/api/retrostock-api'

/** Paginated envelope returned by GET /v2/backtest/strategies/{id}/executions. */
export interface StrategyExecutionsPage {
  items: ExecutionRecord[]
  pageSize: number
  page?: number
  total?: number
  nextCursor?: string | null
}

/** Safe list payload: intentionally excludes producer errors and storage pointers. */
export type ExecutionListSummary = Pick<
  ExecutionRecord,
  | 'executionId'
  | 'strategyId'
  | 'strategyName'
  | 'executionType'
  | 'status'
  | 'triggeredAt'
  | 'symbolCount'
  | 'totalResults'
  | 'completedResults'
  | 'executionSummary'
  | 'filterPreset'
  | 'runConfig'
>

/** Per-strategy latest lookup can fail independently while the batch succeeds. */
export interface UnavailableExecutionSummary {
  unavailable: true
}

export type LatestExecutionSummary = ExecutionListSummary | UnavailableExecutionSummary
export type LatestExecutionsByStrategy = Readonly<Record<string, LatestExecutionSummary | null>>

export function isUnavailableExecutionSummary(
  value: unknown,
): value is UnavailableExecutionSummary {
  return (
    typeof value === 'object' &&
    value !== null &&
    'unavailable' in value &&
    value.unavailable === true
  )
}

export function isExecutionSummaryInFlight(
  value: LatestExecutionSummary | null | undefined,
): boolean {
  return (
    !!value &&
    !isUnavailableExecutionSummary(value) &&
    (value.status === 'PENDING' || value.status === 'RUNNING')
  )
}

export interface RecentExecutionsPage {
  items: ExecutionListSummary[]
  pageSize: number
  nextCursor?: string | null
}

/** Extract `.data` from the response envelope `{ success, data, meta }`. */
function unwrapEnvelope<T = unknown>(raw: unknown): T {
  if (raw != null && typeof raw === 'object' && 'success' in raw && 'data' in raw) {
    return (raw as { data: T }).data
  }
  return raw as T
}

/**
 * RTK Query API for the Backtest service.
 * Handles: strategies, versions, variants, snippets, favorites, results.
 * Auth: accessToken (same base as edgeUsersApi)
 */
export const edgeBacktestApi = createApi({
  reducerPath: 'edgeBacktestApi',
  baseQuery: createAuthBaseQuery(API.users, 'access'),
  tagTypes: [
    'Strategies',
    'Versions',
    'Variants',
    'Snippets',
    'Favorites',
    'Results',
    'Executions',
    'Journal',
    'FilterPresets',
    'Metrics',
  ],
  endpoints: (builder) => ({
    // ── Strategies ──

    listStrategies: builder.query<StrategyListItem[], void>({
      query: () => '/v2/backtest/strategies',
      transformResponse: (raw) => strategyListItemSchema.array().parse(unwrapEnvelope(raw)),
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: 'Strategies' as const, id: s.id })),
              { type: 'Strategies', id: 'LIST' },
            ]
          : [{ type: 'Strategies', id: 'LIST' }],
    }),

    getStrategy: builder.query<Strategy, string>({
      query: (id) => `/v2/backtest/strategies/${encodeURIComponent(id)}`,
      transformResponse: (raw) => strategySchema.parse(unwrapEnvelope(raw)),
      providesTags: (_result, _error, id) => [{ type: 'Strategies', id }],
    }),

    createStrategy: builder.mutation<
      Strategy,
      {
        name: string
        description?: string
        nodes?: unknown[]
        edges?: unknown[]
        tags?: string[]
        script?: StrategyScript
        // Every field optional: a new strategy carries only the editor the
        // person chose on the New Strategy page, and the layout seeds later.
        uiState?: {
          activePanes?: string[]
          dividerPosition?: number
          editorMode?: 'form' | 'canvas' | 'code'
        }
      }
    >({
      query: (body) => ({
        url: '/v2/backtest/strategies',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => strategySchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: [{ type: 'Strategies', id: 'LIST' }],
    }),

    updateStrategy: builder.mutation<
      Strategy,
      {
        id: string
        name?: string
        description?: string
        nodes?: unknown[]
        edges?: unknown[]
        expectedGraphRevision?: number
        tags?: string[]
        script?: StrategyScript
        // Every field optional: a new strategy carries only the editor the
        // person chose on the New Strategy page, and the layout seeds later.
        uiState?: {
          activePanes?: string[]
          dividerPosition?: number
          editorMode?: 'form' | 'canvas' | 'code'
        }
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (raw) => strategySchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Strategies', id },
        { type: 'Strategies', id: 'LIST' },
      ],
    }),

    deleteStrategy: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Strategies', id },
        { type: 'Strategies', id: 'LIST' },
        { type: 'Versions', id },
        { type: 'Variants', id },
        { type: 'Results', id },
      ],
    }),

    duplicateStrategy: builder.mutation<Strategy, string>({
      query: (id) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(id)}/duplicate`,
        method: 'POST',
      }),
      transformResponse: (raw) => strategySchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: [{ type: 'Strategies', id: 'LIST' }],
    }),

    // ── Versions ──

    listVersions: builder.query<StrategyVersion[], string>({
      query: (strategyId) => `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/versions`,
      transformResponse: (raw) => strategyVersionSchema.array().parse(unwrapEnvelope(raw)),
      providesTags: (_result, _error, strategyId) => [{ type: 'Versions', id: strategyId }],
    }),

    createVersion: builder.mutation<
      StrategyVersion,
      {
        strategyId: string
        name: string
        description?: string
        nodes: unknown[]
        edges: unknown[]
        script?: StrategyScript
      }
    >({
      query: ({ strategyId, ...body }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/versions`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => strategyVersionSchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { strategyId }) => [{ type: 'Versions', id: strategyId }],
    }),

    rollbackVersion: builder.mutation<
      Strategy,
      { strategyId: string; versionId: string; expectedGraphRevision: number }
    >({
      query: ({ strategyId, versionId, expectedGraphRevision }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/rollback`,
        method: 'POST',
        body: { versionId, expectedGraphRevision },
      }),
      transformResponse: (raw) => strategySchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { strategyId }) => [
        { type: 'Strategies', id: strategyId },
        { type: 'Strategies', id: 'LIST' },
      ],
    }),

    // ── Variants ──

    listVariants: builder.query<StrategyVariant[], string>({
      query: (strategyId) => `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/variants`,
      transformResponse: (raw) => strategyVariantSchema.array().parse(unwrapEnvelope(raw)),
      providesTags: (_result, _error, strategyId) => [{ type: 'Variants', id: strategyId }],
    }),

    createVariant: builder.mutation<
      StrategyVariant,
      {
        strategyId: string
        name: string
        description?: string
        nodes: unknown[]
        edges: unknown[]
        script?: StrategyScript
      }
    >({
      query: ({ strategyId, ...body }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/variants`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => strategyVariantSchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { strategyId }) => [{ type: 'Variants', id: strategyId }],
    }),

    // ── Snippets ──

    listSnippets: builder.query<Snippet[], void>({
      query: () => '/v2/backtest/snippets',
      transformResponse: (raw) => snippetSchema.array().parse(unwrapEnvelope(raw)),
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: 'Snippets' as const, id: s.id })),
              { type: 'Snippets', id: 'LIST' },
            ]
          : [{ type: 'Snippets', id: 'LIST' }],
    }),

    createSnippet: builder.mutation<Snippet, Omit<Snippet, 'id'>>({
      query: (body) => ({
        url: '/v2/backtest/snippets',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => snippetSchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: [{ type: 'Snippets', id: 'LIST' }],
    }),

    deleteSnippet: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v2/backtest/snippets/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Snippets', id },
        { type: 'Snippets', id: 'LIST' },
      ],
    }),

    // ── Favorites ──

    getFavorites: builder.query<{ nodeTypes: string[] }, void>({
      query: () => '/v2/backtest/favorites',
      transformResponse: (raw) => unwrapEnvelope<{ nodeTypes: string[] }>(raw),
      providesTags: ['Favorites'],
    }),

    toggleFavorite: builder.mutation<{ nodeTypes: string[] }, string>({
      query: (nodeType) => ({
        url: '/v2/backtest/favorites/toggle',
        method: 'PUT',
        body: { nodeType },
      }),
      transformResponse: (raw) => unwrapEnvelope<{ nodeTypes: string[] }>(raw),
      invalidatesTags: ['Favorites'],
    }),

    // ── Executions ──

    listStrategyExecutions: builder.query<
      StrategyExecutionsPage,
      {
        strategyId: string
        executionType?: string
        pagination?: 'offset' | 'cursor'
        page?: number
        pageSize?: number
        cursor?: string
      }
    >({
      query: ({ strategyId, executionType, pagination, page, pageSize, cursor }) => {
        const params = new URLSearchParams()
        if (executionType) params.set('execution_type', executionType)
        if (pagination) params.set('pagination', pagination)
        if (page) params.set('page', String(page))
        if (pageSize) params.set('page_size', String(pageSize))
        if (cursor) params.set('cursor', cursor)
        const qs = params.toString()
        return `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/executions${qs ? `?${qs}` : ''}`
      },
      transformResponse: (raw) => {
        const data = unwrapEnvelope<{
          items: ExecutionRecord[]
          page?: number
          page_size: number
          total?: number
          next_cursor?: string | null
        }>(raw)
        const result: StrategyExecutionsPage = {
          items: data.items,
          pageSize: data.page_size,
        }
        if (data.page != null) result.page = data.page
        if (data.total != null) result.total = data.total
        if ('next_cursor' in data) result.nextCursor = data.next_cursor
        return result
      },
      providesTags: (_result, _error, { strategyId }) => [
        { type: 'Executions' as const, id: strategyId },
      ],
    }),

    /** Newest batch run per strategy for the caller, keyed by strategy id;
     *  null for a strategy that never ran. One request for the whole list. */
    getLatestExecutions: builder.query<
      LatestExecutionsByStrategy,
      { strategyIds: string[]; cacheScope?: string }
    >({
      query: ({ strategyIds }) => {
        const params = new URLSearchParams()
        for (const id of new Set(strategyIds)) params.append('strategy_ids', id)
        return `/v2/backtest/strategies/latest-executions?${params}`
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs.cacheScope ?? 'default'}-${JSON.stringify([...new Set(queryArgs.strategyIds)].sort())}`,
      transformResponse: (raw) => unwrapEnvelope<LatestExecutionsByStrategy>(raw),
      providesTags: [{ type: 'Executions' as const, id: 'LATEST' }],
    }),

    /** The caller's newest batch runs across every strategy, newest first,
     *  each carrying `strategyName`. Feeds the Recent Runs page. */
    getRecentExecutions: builder.query<RecentExecutionsPage, { pageSize: number; cursor?: string }>(
      {
        query: ({ pageSize, cursor }) => {
          const params = new URLSearchParams({ pagination: 'cursor', page_size: String(pageSize) })
          if (cursor) params.set('cursor', cursor)
          return `/v2/backtest/executions/recent?${params}`
        },
        transformResponse: (raw) => {
          const data = unwrapEnvelope<{
            items: ExecutionListSummary[]
            page_size: number
            next_cursor?: string | null
          }>(raw)
          return {
            items: data.items,
            pageSize: data.page_size,
            ...(data.next_cursor !== undefined ? { nextCursor: data.next_cursor } : {}),
          }
        },
        providesTags: [{ type: 'Executions' as const, id: 'RECENT' }],
      },
    ),

    getExecution: builder.query<ExecutionRecord, string>({
      query: (executionId) => `/v2/backtest/execution/${encodeURIComponent(executionId)}`,
      transformResponse: (raw) => unwrapEnvelope<ExecutionRecord>(raw),
      providesTags: (_result, _error, id) => [{ type: 'Executions' as const, id }],
    }),

    getExecutionContext: builder.query<
      ExecutionContext,
      { batchExecutionId: string; ticker: string; targetDate: string }
    >({
      query: ({ batchExecutionId, ticker, targetDate }) =>
        `/v2/backtest/execution/${encodeURIComponent(batchExecutionId)}/result/${encodeURIComponent(ticker)}/${encodeURIComponent(targetDate)}`,
      transformResponse: (raw) => unwrapEnvelope<ExecutionContext>(raw),
    }),

    /** The graph the engine actually ran for an execution. Stripped from
     *  every other execution response for size; fetched once by the Report
     *  to derive stop prices for unit-mode sizing. */
    getExecutionStrategySnapshot: builder.query<
      {
        executionId: string
        nodes: Array<Record<string, unknown>>
        edges: Array<Record<string, unknown>>
      },
      string
    >({
      query: (executionId) =>
        `/v2/backtest/execution/${encodeURIComponent(executionId)}/strategy-snapshot`,
      transformResponse: (raw) =>
        unwrapEnvelope<{
          executionId: string
          nodes: Array<Record<string, unknown>>
          edges: Array<Record<string, unknown>>
        }>(raw),
    }),

    /** Write the Report's allocator settings and allocated summary back onto
     *  the execution's run config. */
    updateRunConfig: builder.mutation<
      RunConfig,
      {
        executionId: string
        portfolioSettings?: Record<string, unknown> | null
        allocatedSummary?: Record<string, unknown> | null
      }
    >({
      query: ({ executionId, ...body }) => ({
        url: `/v2/backtest/execution/${encodeURIComponent(executionId)}/run-config`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (raw) => unwrapEnvelope<RunConfig>(raw),
      invalidatesTags: (_result, _error, { executionId }) => [
        { type: 'Executions' as const, id: executionId },
      ],
    }),

    mergeExecution: builder.mutation<MergeResult, string>({
      query: (executionId) => ({
        url: `/v2/backtest/execution/${encodeURIComponent(executionId)}/merge`,
        method: 'POST',
      }),
      transformResponse: (raw) => unwrapEnvelope<MergeResult>(raw),
    }),

    // ── Results ──

    getStrategyMetrics: builder.query<StrategyMetrics, string>({
      // Two-step: the API returns a cheap { summary, metricsUrl } (no S3 read
      // server-side); we fetch the big results[]/tickers[] doc directly from S3
      // via the presigned URL, keeping that cost off the API Lambda.
      async queryFn(strategyId, _api, _extraOptions, baseQuery) {
        const res = await baseQuery(
          `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/metrics`,
        )
        if (res.error) return { error: res.error }
        const envelope = unwrapEnvelope<{
          strategyId?: string
          updatedAt?: string | null
          summary?: unknown
          metricsUrl?: string | null
        }>(res.data)
        if (envelope?.metricsUrl) {
          try {
            const docRes = await fetch(envelope.metricsUrl)
            if (docRes.ok) {
              const doc = await docRes.json()
              return { data: strategyMetricsSchema.parse(doc) }
            }
          } catch {
            // fall through to summary-only
          }
        }
        // Degrade gracefully: headline summary only, no results/tickers.
        return {
          data: strategyMetricsSchema.parse({
            strategyId: envelope?.strategyId ?? strategyId,
            updatedAt: envelope?.updatedAt ?? undefined,
            summary: envelope?.summary ?? undefined,
            results: [],
            tickers: [],
          }),
        }
      },
      providesTags: (_result, _error, strategyId) => [{ type: 'Metrics', id: strategyId }],
    }),

    listResults: builder.query<BacktestResultRecord[], string>({
      query: (strategyId) => `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/results`,
      transformResponse: (raw) => backtestResultSchema.array().parse(unwrapEnvelope(raw)),
      providesTags: (_result, _error, strategyId) => [{ type: 'Results', id: strategyId }],
    }),

    saveResult: builder.mutation<
      BacktestResultRecord,
      { strategyId: string } & Partial<Omit<BacktestResultRecord, 'id' | 'createdAt'>>
    >({
      query: ({ strategyId, ...body }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/results`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => backtestResultSchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { strategyId }) => [{ type: 'Results', id: strategyId }],
    }),

    getResult: builder.query<BacktestResultRecord, { strategyId: string; resultId: string }>({
      query: ({ strategyId, resultId }) =>
        `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/results/${encodeURIComponent(resultId)}`,
      transformResponse: (raw) => backtestResultSchema.parse(unwrapEnvelope(raw)),
    }),

    presignUrl: builder.mutation<{ url: string }, { uri: string }>({
      query: (body) => ({
        url: '/v2/backtest/presign',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => unwrapEnvelope<{ url: string }>(raw),
    }),

    // ── Champion ──

    setChampionResult: builder.mutation<Strategy, { strategyId: string; resultId: string }>({
      query: ({ strategyId, resultId }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/champion`,
        method: 'POST',
        body: { resultId },
      }),
      transformResponse: (raw) => strategySchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { strategyId }) => [
        { type: 'Strategies', id: strategyId },
        { type: 'Strategies', id: 'LIST' },
      ],
    }),

    // ── Journal ──

    listJournalEntries: builder.query<JournalEntry[], { strategyId: string; resultId: string }>({
      query: ({ strategyId, resultId }) =>
        `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/results/${encodeURIComponent(resultId)}/journal`,
      transformResponse: (raw) => journalEntrySchema.array().parse(unwrapEnvelope(raw)),
      providesTags: (_result, _error, { strategyId, resultId }) => [
        { type: 'Journal', id: `${strategyId}:${resultId}` },
      ],
    }),

    saveJournalEntry: builder.mutation<
      JournalEntry,
      {
        strategyId: string
        resultId: string
        tradeIndex: number
        notes?: string
        sentiment?: string
        tags?: string[]
        lessonsLearned?: string
      }
    >({
      query: ({ strategyId, ...body }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/journal`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => journalEntrySchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { strategyId, resultId }) => [
        { type: 'Journal', id: `${strategyId}:${resultId}` },
      ],
    }),

    updateJournalEntry: builder.mutation<
      JournalEntry,
      {
        strategyId: string
        resultId: string
        tradeIndex: number
        notes?: string
        sentiment?: string
        tags?: string[]
        lessonsLearned?: string
      }
    >({
      query: ({ strategyId, resultId, tradeIndex, ...body }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/results/${encodeURIComponent(resultId)}/journal/${tradeIndex}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (raw) => journalEntrySchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { strategyId, resultId }) => [
        { type: 'Journal', id: `${strategyId}:${resultId}` },
      ],
    }),

    deleteJournalEntry: builder.mutation<
      void,
      { strategyId: string; resultId: string; tradeIndex: number }
    >({
      query: ({ strategyId, resultId, tradeIndex }) => ({
        url: `/v2/backtest/strategies/${encodeURIComponent(strategyId)}/results/${encodeURIComponent(resultId)}/journal/${tradeIndex}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { strategyId, resultId }) => [
        { type: 'Journal', id: `${strategyId}:${resultId}` },
      ],
    }),

    // ── Filter Presets ──

    listFilterPresets: builder.query<FilterPreset[], { limit?: number } | void>({
      query: (params) => {
        const limit = params && 'limit' in params ? params.limit : undefined
        const qs = limit ? `?limit=${limit}` : ''
        return `/v2/backtest/filter-presets${qs}`
      },
      transformResponse: (raw) => filterPresetSchema.array().parse(unwrapEnvelope(raw)),
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: 'FilterPresets' as const, id: p.filterId })),
              { type: 'FilterPresets', id: 'LIST' },
            ]
          : [{ type: 'FilterPresets', id: 'LIST' }],
    }),

    getFilterPreset: builder.query<FilterPreset, string>({
      query: (filterId) => `/v2/backtest/filter-presets/${encodeURIComponent(filterId)}`,
      transformResponse: (raw) => filterPresetSchema.parse(unwrapEnvelope(raw)),
      providesTags: (_result, _error, id) => [{ type: 'FilterPresets', id }],
    }),

    createFilterPreset: builder.mutation<
      FilterPreset,
      {
        name: string
        source: 'manual' | 'batch_execution'
        inputSource: 'filters' | 'csv'
        filters?: Record<string, unknown> | null
        filtersVersion?: string
        csvFileName?: string | null
        inputCount: number
        inputs?: Array<{ ticker: string; target_date: string }> | null
        description?: string | null
        universeName?: string | null
      }
    >({
      query: (body) => ({
        url: '/v2/backtest/filter-presets',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => filterPresetSchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: [{ type: 'FilterPresets', id: 'LIST' }],
    }),

    updateFilterPreset: builder.mutation<
      FilterPreset,
      { filterId: string; name?: string; description?: string }
    >({
      query: ({ filterId, ...body }) => ({
        url: `/v2/backtest/filter-presets/${encodeURIComponent(filterId)}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (raw) => filterPresetSchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: (_result, _error, { filterId }) => [
        { type: 'FilterPresets', id: filterId },
        { type: 'FilterPresets', id: 'LIST' },
      ],
    }),

    deleteFilterPreset: builder.mutation<void, string>({
      query: (filterId) => ({
        url: `/v2/backtest/filter-presets/${encodeURIComponent(filterId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, filterId) => [
        { type: 'FilterPresets', id: filterId },
        { type: 'FilterPresets', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListStrategiesQuery,
  useGetStrategyQuery,
  useLazyGetStrategyQuery,
  useGetStrategyMetricsQuery,
  useCreateStrategyMutation,
  useUpdateStrategyMutation,
  useDeleteStrategyMutation,
  useDuplicateStrategyMutation,
  useListVersionsQuery,
  useCreateVersionMutation,
  useRollbackVersionMutation,
  useListVariantsQuery,
  useCreateVariantMutation,
  useListSnippetsQuery,
  useCreateSnippetMutation,
  useDeleteSnippetMutation,
  useGetFavoritesQuery,
  useToggleFavoriteMutation,
  useListStrategyExecutionsQuery,
  useGetLatestExecutionsQuery,
  useLazyGetLatestExecutionsQuery,
  useGetRecentExecutionsQuery,
  useGetExecutionQuery,
  useGetExecutionContextQuery,
  useGetExecutionStrategySnapshotQuery,
  useUpdateRunConfigMutation,
  useMergeExecutionMutation,
  useListResultsQuery,
  useSaveResultMutation,
  useGetResultQuery,
  usePresignUrlMutation,
  useSetChampionResultMutation,
  useListJournalEntriesQuery,
  useSaveJournalEntryMutation,
  useUpdateJournalEntryMutation,
  useDeleteJournalEntryMutation,
  useListFilterPresetsQuery,
  useGetFilterPresetQuery,
  useCreateFilterPresetMutation,
  useUpdateFilterPresetMutation,
  useDeleteFilterPresetMutation,
} = edgeBacktestApi
